/* NUTRILINQ — scroll storytelling engine
 *
 * [data-nl-reveal]        gets .is-in once 15% of it is on screen (one-shot).
 * [data-nl-track]         a tall scroll track. While it's on screen, its
 *                         progress through the viewport (0 → 1) is written to
 *                         the CSS variable --p. CSS turns --p into transforms.
 * [data-nl-steps="n"]     on a track: also writes data-step="0…n-1" so CSS can
 *                         switch between discrete states.
 * [data-nl-weights="1,1.6,1"]  on a stepped track: how long each step lasts,
 *                         relative to the others (default: equal). Each
 *                         step's own progress (0 → 1) is written to --p0,
 *                         --p1, … so CSS can pan / fill within a step.
 * [data-nl-enter]         progress of an element *entering* the viewport
 *                         (0 when its top hits the bottom, 1 when its top
 *                         reaches the top) → --enter. Used for gentle scale-ins.
 *
 * Only transform/opacity are driven from these values (see nl-home.css).
 * Reduced motion: nothing is tracked; CSS shows every final state.
 */
(function () {
  'use strict';
  if (window.__nlStory) return;   // loaded once per page, however many sections include it
  window.__nlStory = true;

  var root = document.documentElement;
  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)');

  function clamp(v) {
    return v < 0 ? 0 : v > 1 ? 1 : v;
  }

  function initReveals(scope) {
    var els = scope.querySelectorAll('[data-nl-reveal]:not(.is-in)');
    if (!els.length) return;
    if (reduced.matches || !('IntersectionObserver' in window)) {
      els.forEach(function (el) {
        el.classList.add('is-in');
      });
      return;
    }
    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-in');
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -5% 0px' }
    );
    els.forEach(function (el) {
      io.observe(el);
    });
  }

  // Step boundaries in 0…1, from data-nl-weights (or equal steps).
  function stopsFor(el, steps) {
    if (el.__nlStops && el.__nlStops.length === steps + 1) return el.__nlStops;
    var w = (el.getAttribute('data-nl-weights') || '').split(',').map(parseFloat);
    if (w.length !== steps || w.some(isNaN)) w = new Array(steps).fill(1);
    var total = w.reduce(function (t, v) { return t + v; }, 0), acc = 0, out = [0];
    w.forEach(function (v) { acc += v; out.push(acc / total); });
    return (el.__nlStops = out);
  }

  var active = new Set();
  var ticking = false;
  var watcher = null;

  function measure() {
    var vh = window.innerHeight;
    active.forEach(function (el) {
      var r = el.getBoundingClientRect();
      if (el.hasAttribute('data-nl-track')) {
        var span = r.height - vh;
        var p = span > 0 ? clamp(-r.top / span) : clamp((vh - r.top) / (vh + r.height));
        el.style.setProperty('--p', p.toFixed(4));
        var steps = parseInt(el.getAttribute('data-nl-steps'), 10);
        if (steps > 0) {
          var stops = stopsFor(el, steps);
          var step = 0;
          for (var i = 0; i < steps; i++) {
            var a = stops[i], b = stops[i + 1];
            el.style.setProperty('--p' + i, clamp((p - a) / (b - a)).toFixed(4));
            if (p >= a) step = i;
          }
          if (el.getAttribute('data-step') !== String(step)) el.setAttribute('data-step', step);
        }
      }
      if (el.hasAttribute('data-nl-enter')) {
        el.style.setProperty('--enter', clamp((vh - r.top) / vh).toFixed(4));
      }
    });
    ticking = false;
  }

  function request() {
    if (!ticking) {
      ticking = true;
      window.requestAnimationFrame(measure);
    }
  }

  function initTracks(scope) {
    var els = scope.querySelectorAll('[data-nl-track], [data-nl-enter]');
    if (!els.length) return;
    if (reduced.matches) {
      els.forEach(function (el) {
        el.style.setProperty('--p', 1);
        el.style.setProperty('--enter', 1);
      });
      return;
    }
    if (!watcher) {
      watcher = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) active.add(entry.target);
            else active.delete(entry.target);
          });
          request();
        },
        { rootMargin: '10% 0px 10% 0px' }
      );
    }
    els.forEach(function (el) {
      el.setAttribute('data-step', 0);
      watcher.observe(el);
    });
    request();
  }

  function init(scope) {
    initReveals(scope || document);
    initTracks(scope || document);
  }

  root.classList.add('nl-js');
  if (reduced.matches) root.classList.add('nl-reduced');

  window.addEventListener('scroll', request, { passive: true });
  window.addEventListener('resize', request);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }

  // Theme editor: re-run when a section is added or re-rendered.
  document.addEventListener('shopify:section:load', function (e) {
    init(e.target);
  });
})();
